import React from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import {
  answersActions,
  RootStateAnswers,
} from "../../../questionnaire/store/answers-slice";
import { biometricInfoType, personalInfoType } from "../../store/helper";
import CustomInputField from "../biometricTab/CustomInputField";
import CustomDatePicker from "../biometricTab/CustomDatePicker";
import ReadOnlyInputField from "../biometricTab/ReadOnlyInputField";
import StyledPhoneInput from "../biometricTab/StyledPhoneInput";
import AddressAutocompleteField from "../biometricTab/AddressAutocompleteField";
import { ParsedAddress } from "../../../../common/utils/googleMaps";

interface Props {
  // "column" — the Biometrics tab first column; "grid" — 3-col modal layout.
  layout?: "column" | "grid";
  openPopperId: string | null;
  setOpenPopperId: React.Dispatch<React.SetStateAction<string | null>>;
  // Gender + Date of birth live in the biometric questionnaire (separate load
  // and save path). Only render them where that data is available (the tab).
  includeBiometricFields?: boolean;
  // Surfaces the address autocomplete's missing parts (street number / postal
  // code) so a parent (the Biometrics tab) can gate its Save button.
  onAddressCompletenessChange?: (missingParts: string[]) => void;
  // The Report/Nutrition/Workout indicators. Default true so the existing call
  // sites are unchanged; the Profile screen passes false and renders them as its
  // own "Client access" card instead.
  includeClientAccess?: boolean;
}

// Read-only checkbox mirroring the Biometrics tab "Client access" indicators.
const CheckBoxItem = ({
  isChecked,
  theme,
}: {
  isChecked: boolean;
  theme: Theme;
}) => (
  <div style={styles.checkBoxItemContainer(theme)}>
    {isChecked && (
      <svg
        width="11"
        height="8"
        viewBox="0 0 11 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3.20703 7.5515C3.83203 8.21391 4.45703 8.08143 4.95703 7.5515L9.95703 2.2522C10.457 1.72227 10.457 0.927377 9.95703 0.397447C9.45703 -0.132482 8.70703 -0.132482 8.20703 0.397447L4.08203 4.76937L2.45703 3.0471C1.95703 2.51717 1.20703 2.51717 0.707031 3.0471C0.207031 3.57703 0.207031 4.37192 0.707031 4.90185L3.20703 7.5515Z"
          fill={theme.colors_ds.NEUTRAL_TEXT_DEFAULT}
        />
      </svg>
    )}
  </div>
);

const BasicInformationFields = ({
  layout = "column",
  openPopperId,
  setOpenPopperId,
  includeBiometricFields = true,
  onAddressCompletenessChange,
  includeClientAccess = true,
}: Props): JSX.Element => {
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const currentDate = new Date();
  const clientData = useSelector((state: RootStateAnswers) => state.answers);
  const isGrid = layout === "grid";

  const birthDate = clientData.biometricQuest?.myAnswers?.birth_date;

  // A Google Places selection fills the whole address block at once; the
  // derived fields (city/state/zip/country) are shown read-only. Mark each
  // filled field dirty so the Biometrics tab's Save button enables.
  const handleAddressSelect = (parsed: ParsedAddress) => {
    const update: Record<string, any> = {
      [personalInfoType.address]: parsed.address,
      [personalInfoType.city]: parsed.city,
      [personalInfoType.stateName]: parsed.stateName,
      [personalInfoType.stateCode]: parsed.stateCode,
      [personalInfoType.countryName]: parsed.countryName,
      [personalInfoType.countryCode]: parsed.countryCode,
      [personalInfoType.zip]: parsed.zip,
    };
    dispatch(answersActions.updatePersonalInfoAnswers(update));
    Object.keys(update).forEach((k) => {
      if (clientData.personalInfoModifiedFields.indexOf(k) === -1) {
        dispatch(answersActions.addPersonalInfoModifiedFields(k));
      }
    });
  };

  return (
    <Box sx={styles.container(isGrid)}>
      <CustomInputField
        type="string"
        category={personalInfoType.firstName}
        openPopperId={openPopperId}
        setOpenPopperId={setOpenPopperId}
      />
      <CustomInputField
        type="string"
        category={personalInfoType.lastName}
        openPopperId={openPopperId}
        setOpenPopperId={setOpenPopperId}
      />
      {includeBiometricFields && (
        <CustomInputField
          type="select"
          category={biometricInfoType.gender}
          openPopperId={openPopperId}
          setOpenPopperId={setOpenPopperId}
        />
      )}
      {includeBiometricFields && (
        <CustomDatePicker
          label={"Date of birth"}
          defaultValue={new Date(birthDate)}
          maxValue={
            new Date(
              currentDate.getFullYear() - 6,
              currentDate.getMonth(),
              currentDate.getDate()
            )
          }
          minValue={
            new Date(
              currentDate.getFullYear() - 110,
              currentDate.getMonth(),
              currentDate.getDate()
            )
          }
          category={biometricInfoType.birth_date}
          setOpenPopperId={setOpenPopperId}
        />
      )}
      <ReadOnlyInputField category={personalInfoType.email} />
      <StyledPhoneInput
        category={personalInfoType.phone}
        label="Phone number"
        openPopperId={openPopperId}
        setOpenPopperId={setOpenPopperId}
      />
      <AddressAutocompleteField
        value={clientData.personalInfoAnswers.address ?? ""}
        isUpdated={
          clientData.personalInfoModifiedFields.indexOf(
            personalInfoType.address
          ) !== -1
        }
        onSelect={handleAddressSelect}
        onCompletenessChange={onAddressCompletenessChange}
      />
      <ReadOnlyInputField category={personalInfoType.city} label="City" />
      {clientData.personalInfoAnswers.stateName ? (
        <ReadOnlyInputField
          category={personalInfoType.stateName}
          label="State"
        />
      ) : null}
      <ReadOnlyInputField category={personalInfoType.zip} label="Zip code" />
      <ReadOnlyInputField
        category={personalInfoType.countryName}
        label="Country"
      />
      {includeClientAccess && (
      <Box sx={styles.togglesRow(isGrid)}>
        <Box sx={styles.toggleItem}>
          <Typography style={styles.text(theme)}>Report:</Typography>
          <CheckBoxItem
            isChecked={clientData.personalInfoInitialAnswers.report}
            theme={theme}
          />
        </Box>
        <Box sx={styles.toggleItem}>
          <Typography style={styles.text(theme)}>Nutrition:</Typography>
          <CheckBoxItem
            isChecked={clientData.personalInfoInitialAnswers.nutrition}
            theme={theme}
          />
        </Box>
        <Box sx={styles.toggleItem}>
          <Typography style={styles.text(theme)}>Workout:</Typography>
          <CheckBoxItem
            isChecked={clientData.personalInfoInitialAnswers.workout}
            theme={theme}
          />
        </Box>
      </Box>
      )}
    </Box>
  );
};

export default BasicInformationFields;

const styles = {
  container: (isGrid: boolean) =>
    isGrid
      ? {
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "16px",
          width: "100%",
        }
      : {
          display: "flex",
          flexDirection: "column" as "column",
          gap: "16px",
          flex: 1,
        },
  togglesRow: (isGrid: boolean) => ({
    display: "flex",
    gap: "24px",
    alignItems: "center",
    justifyContent: isGrid ? "flex-start" : "space-between",
    ...(isGrid ? { gridColumn: "1 / -1" } : {}),
  }),
  toggleItem: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  text: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
  }),
  checkBoxItemContainer: (theme: Theme) => ({
    width: "16px",
    height: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_TEXT_DEFAULT}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
};
